/**
 * App 引导组件
 * 使用 Driver.js 提供交互式应用引导
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { driver } from 'driver.js';
import { useTranslation } from 'react-i18next';
import { 
  shouldShowAppOnboarding, 
  markAppOnboardingCompleted 
} from '../../../utils/appOnboardingUtils';

// 导入 Driver.js 样式
import 'driver.js/dist/driver.css';

interface AppOnboardingProps {
  /** 是否为首次用户 */
  isFirstTime?: boolean;
  /** 引导完成回调 */
  onComplete?: () => void;
  /** 跳过引导回调 */
  onSkip?: () => void;
  /** 是否自动开始引导 */
  autoStart?: boolean;
}

export const AppOnboarding: React.FC<AppOnboardingProps> = ({
  isFirstTime = false,
  onComplete,
  onSkip,
  autoStart = false
}) => {
  // 检查环境变量是否启用了应用引导功能
  const isOnboardingEnabled = import.meta.env.VITE_APP_ONBOARDING_ENABLED === 'true';

  // 如果未启用应用引导，直接返回 null
  if (!isOnboardingEnabled) {
    return null;
  }

  const { t } = useTranslation(['v3/appOnboarding']);
  const [driverInstance, setDriverInstance] = useState<any | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // 等待元素出现的工具函数
  const waitForElement = useCallback((selector: string, timeout = 5000): Promise<Element> => {
    return new Promise((resolve, reject) => {
      const element = document.querySelector(selector);
      if (element) {
        resolve(element);
        return;
      }

      const observer = new MutationObserver(() => {
        const element = document.querySelector(selector);
        if (element) {
          observer.disconnect();
          resolve(element);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      setTimeout(() => {
        observer.disconnect();
        reject(new Error(`Element ${selector} not found within ${timeout}ms`));
      }, timeout);
    });
  }, []);

  // 引导步骤配置
  const tourSteps = useMemo(() => [
    {
      element: 'body',
      popover: {
        title: t('welcome.title'),
        description: t('welcome.description'),
        position: 'center' as const,
        showButtons: ['next', 'close'],
        nextBtnText: t('buttons.next'),
        closeBtnText: t('buttons.close')
      }
    },
    {
      element: '[data-testid="welcome-project"]',
      popover: {
        title: t('steps.exampleProject.title'),
        description: t('steps.exampleProject.description'),
        position: 'right' as const,
        showButtons: ['previous', 'next'],
        nextBtnText: t('buttons.next'),
        prevBtnText: t('buttons.previous')
      }
    },
    {
      element: '[data-testid="add-task-button"]',
      popover: {
        title: t('steps.createTask.title'),
        description: t('steps.createTask.description'),
        position: 'bottom' as const,
        showButtons: ['previous', 'next'],
        nextBtnText: t('buttons.next'),
        prevBtnText: t('buttons.previous')
      },
      onHighlighted: () => {
        console.log('[AppOnboarding] Highlighting add task button');
      }
    },
    {
      element: '[data-testid="welcome-project"] [data-task-id]:first-child',
      popover: {
        title: t('steps.completeTask.title'),
        description: t('steps.completeTask.description'),
        position: 'right' as const,
        showButtons: ['previous', 'next'],
        nextBtnText: t('buttons.next'),
        prevBtnText: t('buttons.previous')
      }
    },
    {
      element: '[data-testid="welcome-project"] [data-task-id]:nth-child(3)',
      popover: {
        title: t('steps.editTask.title'),
        description: t('steps.editTask.description'),
        position: 'right' as const,
        showButtons: ['previous', 'next'],
        nextBtnText: t('buttons.next'),
        prevBtnText: t('buttons.previous')
      }
    },
    {
      element: '[data-testid="chat-tab"]',
      popover: {
        title: t('steps.aiChat.title'),
        description: t('steps.aiChat.description'),
        position: 'top' as const,
        showButtons: ['previous', 'next'],
        nextBtnText: t('buttons.next'),
        prevBtnText: t('buttons.previous')
      }
    },
    {
      element: '[data-testid="chat-input"]',
      popover: {
        title: t('steps.aiCreate.title'),
        description: t('steps.aiCreate.description'),
        position: 'top' as const,
        showButtons: ['previous', 'next'],
        nextBtnText: t('buttons.next'),
        prevBtnText: t('buttons.previous')
      }
    },
    {
      element: '[data-testid="chat-input"]',
      popover: {
        title: t('steps.aiRearrange.title'),
        description: t('steps.aiRearrange.description'),
        position: 'top' as const,
        showButtons: ['previous', 'next'],
        nextBtnText: t('buttons.next'),
        prevBtnText: t('buttons.previous')
      }
    },
    {
      element: '[data-testid="taskv2-tab"]',
      popover: {
        title: t('steps.backToTasks.title'),
        description: t('steps.backToTasks.description'),
        position: 'top' as const,
        showButtons: ['previous', 'next'],
        nextBtnText: t('buttons.next'),
        prevBtnText: t('buttons.previous')
      }
    },
    {
      element: 'body',
      popover: {
        title: t('steps.complete.title'),
        description: t('steps.complete.description'),
        position: 'center' as const,
        showButtons: ['close'],
        closeBtnText: t('buttons.finish')
      }
    }
  ], [t]);

  // 创建 Driver 实例
  const createDriver = useCallback(() => {
    const driverObj = driver({
      showProgress: true,
      allowClose: true,
      overlayClickNext: false,
      smoothScroll: true,
      steps: tourSteps,
      onCloseClick: (element, step, options) => {
        console.log('[AppOnboarding] Tour closed by user', { stepIndex: options.state.activeIndex });
        markAppOnboardingCompleted();
        setIsVisible(false);
        onSkip?.();
      },
      onDestroyed: (element, step, options) => {
        console.log('[AppOnboarding] Tour destroyed', {
          stepIndex: options.state.activeIndex,
          totalSteps: tourSteps.length
        });
        // 只有在完成所有步骤后才标记为完成
        // activeIndex 是从0开始的，所以最后一步是 length - 1
        if (options.state.activeIndex === tourSteps.length - 1) {
          console.log('[AppOnboarding] Tour completed successfully');
          markAppOnboardingCompleted();
          setIsVisible(false);
          onComplete?.();
        } else {
          console.log('[AppOnboarding] Tour destroyed prematurely, not marking as completed');
        }
      }
    });

    return driverObj;
  }, [tourSteps, onComplete, onSkip]);

  // 开始引导
  const startTour = useCallback(() => {
    console.log('[AppOnboarding] Starting tour...');

    const driverObj = createDriver();
    setDriverInstance(driverObj);
    setIsVisible(true);

    // 开始引导
    driverObj.drive();
  }, [createDriver]);

  // 跳过引导
  const skipTour = useCallback(() => {
    console.log('[AppOnboarding] Skipping tour...');
    markAppOnboardingCompleted();
    setIsVisible(false);
    onSkip?.();
  }, [onSkip]);

  // 检查是否需要显示引导
  useEffect(() => {
    if (autoStart && shouldShowAppOnboarding()) {
      // 延迟一点时间，确保页面元素已加载
      const timer = setTimeout(() => {
        startTour();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [autoStart, startTour]);

  // 清理 Driver 实例
  useEffect(() => {
    return () => {
      if (driverInstance) {
        driverInstance.destroy();
      }
    };
  }, [driverInstance]);

  // 如果不需要显示引导，返回 null
  if (!isVisible && !shouldShowAppOnboarding()) {
    return null;
  }

  // 如果是首次用户且需要显示引导，显示欢迎横幅
  if (isFirstTime && shouldShowAppOnboarding() && !isVisible) {
    return (
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-white dark:bg-gray-800 shadow-lg rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t('welcome.title')}
            </p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={startTour}
              className="px-3 py-1 bg-coral-500 text-white text-sm rounded hover:bg-coral-600 transition-colors"
            >
              {t('welcome.startButton')}
            </button>
            <button
              onClick={skipTour}
              className="px-3 py-1 text-gray-500 dark:text-gray-400 text-sm hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              {t('welcome.skipButton')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AppOnboarding;
